import { Component, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { PuntoVenta } from 'app/models/puntoventa.models';
import { Store, Tienda, CreateStoreDto, UpdateStoreDto, StoreType, storeTypeToIsCatalog, isCatalogToStoreType } from 'app/models/tienda.models';
import { Integration, Integration_Types, CreateIntegrationDto, UpdateIntegrationDto } from 'app/models/integration.models';
import { 
  StoreIntegration, 
  CreateStoreIntegrationDto, 
  UpdateStoreIntegrationDto,
  DEFAULT_INTEGRATION_CONFIGS 
} from 'app/models/store-integration.models';
import { Company } from 'app/models/company.models';
import { ArchivosService } from 'app/services/archivos.service';
import { EmpresaService } from 'app/services/empresa.service';
import { PtoventaService } from 'app/services/ptoventa.service';
import { TiendaService } from 'app/services/tienda.service';
import { IntegrationService } from 'app/services/integration.service';
import { StoreIntegrationService } from 'app/services/store-integration.service';
import { UtilService } from 'app/shared/utils.service';
import { IntegrationDialogComponent, IntegrationDialogData } from './integration-dialog.component';
import { 
  IntegrationConfigDialogComponent, 
  IntegrationConfigDialogData 
} from './integration-config-dialog.component';

@Component({
  selector: 'app-tienda',
  templateUrl: './tienda.component.html',
  styleUrls: ['./tienda.component.css']
})
export class TiendaComponent {

  @ViewChild('bannerInput') bannerInput!: ElementRef<HTMLInputElement>;
  @ViewChild('faviconInput') faviconInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoInput') logoInput!: ElementRef<HTMLInputElement>;

  public myForm: FormGroup;
  puntoVentas: PuntoVenta[] = [];
  store: Store | null = null;
  tienda: Tienda; // Mantenido para compatibilidad
  idTienda: number;

  // Store Integration properties
  availableIntegrations: Integration[] = []; // Todas las integraciones disponibles
  storeIntegrations: StoreIntegration[] = []; // Integraciones activas de esta tienda
  mergedIntegrations: any[] = []; // Vista combinada de integraciones con estado de tienda
  loadingIntegrations = false;
  acordarConVendedorActive = false;

  // Custom Domain properties
  useCustomDomain = false;

  // Loading state for save operation
  saving = false;

  // Loading states for image uploads
  bannerUploading = false;
  faviconUploading = false;
  logoUploading = false;

  // Tab navigation
  activeTab = 'general';

  // Store types enum for template
  StoreType = StoreType;

  constructor(
    private fb: FormBuilder,
    private empresaService: EmpresaService,
    private archivosService: ArchivosService,
    private ptoVentaService: PtoventaService,
    private _snackBar: MatSnackBar,
    private tiendaService: TiendaService,
    private integrationService: IntegrationService,
    private storeIntegrationService: StoreIntegrationService,
    private utilService: UtilService,
    private dialog: MatDialog
  ) {
    const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
    if (sidebar !== undefined) {
      sidebar.innerHTML = "Tienda Online";
    }
    this.myForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(45), this.utilService.stringMaxLength]],
      title: ['', [Validators.maxLength(100)]],
      subtitle: ['', [Validators.maxLength(200)]],
      bannerUrl: ['', [Validators.maxLength(500)]],
      faviconUrl: ['', [Validators.maxLength(500)]],
      available: ['1'],
      status: ['', [Validators.maxLength(45)]],
      address: ['', [Validators.maxLength(45)]],
      email: ['', [
          Validators.email,
          Validators.maxLength(45)
        ]
      ],
      phone: ['', [Validators.maxLength(45)]],
      cellphone: ['', [Validators.maxLength(45)]],
      logo: ['', [Validators.maxLength(500)]], // Aumentar límite para URLs de imágenes
      ipAddress: ['', [Validators.maxLength(45)]],
      domain: ['', [Validators.maxLength(45)]],
      type: [StoreType.CATALOG],
      isCatalog: [true], // Campo calculado para compatibilidad
      companyId: [1, [Validators.required]],
      salesPointId: ['vacio', Validators.required],
      integrations: [[]]
    });
    this.myForm.controls["email"].setValidators(this.utilService.validarEmails);
    
    // Agregar listener para actualizar el dominio cuando cambie el nombre
    this.myForm.get('name')?.valueChanges.subscribe(() => {
      // Trigger change detection para actualizar el dominio por defecto mostrado
      setTimeout(() => {
        // El dominio se actualiza automáticamente a través del método getDefaultDomain()
      }, 0);
    });
    
    this.getPuntosVentas();
    this.loadEmpresaData(); // Cargar datos de la empresa primero
    this.getTienda(); // Las integraciones se cargarán después de obtener la tienda
  
  }

  /**
   * Carga los datos de la empresa para establecer valores por defecto
   */
  loadEmpresaData() {
    this.empresaService.getEmpresaConfig().subscribe(
      (empresa: Company) => {
        // Si existe razón social y el campo nombre está vacío, establecerla como valor por defecto
        if (empresa && empresa.socialReason && !this.myForm.get('name')?.value) {
          this.myForm.get('name')?.setValue(empresa.socialReason);
          
          // Actualizar también el dominio por defecto si está disponible el DOM
          // Esto asegura que el dominio se actualice automáticamente
          setTimeout(() => {
            if (this.getDefaultDomain) {
              // Trigger change detection para actualizar el dominio mostrado
              this.myForm.get('name')?.updateValueAndValidity();
            }
          }, 0);
        }
      },
      (err) => {
        console.warn('Error al cargar datos de la empresa:', err);
        // Si no se puede cargar la empresa, usar un valor por defecto
        if (!this.myForm.get('name')?.value) {
          this.myForm.get('name')?.setValue('Mi Tienda Online');
        }
      }
    );
  }

  getTienda() {
    this.tiendaService.getStore().subscribe(
      (res: Store) => {
        if (res) {
          this.store = res;
          if (res?.id) {
            this.idTienda = res.id;
          }
          
          // Mapear de Store a formato del formulario
          const formData = {
            id: res.id,
            name: res.name || '',
            title: res.title || '',
            subtitle: res.subtitle || '',
            bannerUrl: res.bannerUrl || '',
            faviconUrl: res.faviconUrl || '',
            // Convertir número a string para el binding con radio buttons
            available: String(res.available),
            status: res.status || 'active',
            address: res.address || '',
            email: res.email || '',
            phone: res.phone || '',
            cellphone: res.cellphone || '',
            logo: res.logo || '',
            ipAddress: res.ipAddress || '',
            domain: res.domain || '',
            // Manejar type y mantener compatibilidad con catalog
            type: res.type,
            companyId: res.companyId || (res.company?.id) || 1,
            salesPointId: res.salesPointId || 'vacio'
          };
          this.myForm.patchValue(formData);
          
          // Detectar si hay un dominio personalizado configurado
          if (res.domain && res.domain.trim() !== '' && !res.domain.includes('.tienda.kocursoft.com')) {
            this.useCustomDomain = true;
          }
          
          // Cargar integraciones de pago después de obtener la tienda
          this.getPaymentIntegrations();
        }
      },
      (err) => {
        console.error('Error al cargar tienda:', err);
        
        // Manejar diferentes tipos de errores
        if (err.status === 404) {
          this.store = null; // Asegurar que store sea null para crear nueva
          
          // Establecer valores por defecto para nueva tienda
          this.myForm.patchValue({
            type: StoreType.CATALOG,
            isCatalog: true,
            available: '1',
            status: 'active'
          });
          
          // Cargar integraciones disponibles aunque no haya tienda aún
          this.getPaymentIntegrations();
        } else {
          // Para cualquier otro error (500, timeout, etc.), NO establecer store como null
          // porque podría existir una tienda pero hay problemas de conectividad
          console.warn('Error al cargar tienda (no es 404). Status:', err.status, 'Error:', err.message);
          this.openSnackBar('Error al cargar los datos de la tienda. Verifique su conexión.', 'Error');
          
          // Mantener el store como undefined/null pero no asumir que no existe
          // El método guardar() debe manejar este caso especial
        }
      }
    )
  }

  guardar() {
    if (!this.isFormValid()) {
      this.openSnackBar('Por favor, completa todos los campos requeridos y selecciona si la tienda estará disponible para el público', 'Error');
      return;
    }

    // Activar estado de carga
    this.saving = true;

    const formValue = this.myForm.getRawValue();
    
    // Preparar datos del store
    const finalDomain = this.useCustomDomain ? formValue.domain : this.getDefaultDomain();
    
    const storeData: CreateStoreDto | UpdateStoreDto = { 
      name: formValue.name,
      title: formValue.title,
      subtitle: formValue.subtitle,
      bannerUrl: formValue.bannerUrl,
      faviconUrl: formValue.faviconUrl,
      available: Number(formValue.available),
      status: formValue.status,
      address: formValue.address,
      email: formValue.email,
      phone: formValue.phone,
      cellphone: formValue.cellphone,
      logo: formValue.logo,
      ipAddress: formValue.ipAddress,
      domain: finalDomain,
      type: formValue.type,
      companyId: formValue.companyId,
      salesPointId: formValue.salesPointId !== 'vacio' ? formValue.salesPointId : undefined
    };
    
    // Lógica simplificada: decidir entre CREATE (POST) o UPDATE (PATCH) basado en formValue.id
    if (this.idTienda) {
      // ✅ ACTUALIZAR: La tienda existe y tiene ID válido → PATCH /v1/store/{id}
      this.tiendaService.updateStore(this.idTienda, storeData as UpdateStoreDto).subscribe(
        (data: Store) => {
          this.store = data;
          this.saving = false;
          this.openSnackBar('Tienda actualizada con éxito', 'Información');
          
          // Recargar integraciones después de actualizar tienda
          this.getPaymentIntegrations();
        },
        (err) => {
          this.saving = false;
          this.handleHttpError(err, 'actualizar tienda');
        }
      );
    } else {
      // ✅ CREAR: No hay tienda o ID es null → POST /v1/store
      this.tiendaService.createStore(storeData as CreateStoreDto).subscribe(
        (data: Store) => {
          this.store = data;
          this.saving = false;
          this.openSnackBar('Tienda creada con éxito', 'Información');
          
          // Recargar integraciones después de crear tienda
          this.getPaymentIntegrations();
        },
        (err) => {
          this.saving = false;
          this.handleHttpError(err, 'crear tienda');
        }
      );
    }
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  // Método ya no necesario - Bootstrap radio buttons con formControlName se manejan automáticamente
  // setDisponible removido

  getPuntosVentas() {
    this.ptoVentaService.getAll().subscribe(
      (data) => {
        this.puntoVentas = data;
        // Si hay puntos de venta disponibles y no hay uno seleccionado
        if (data && data.length > 0 && this.myForm.get('salesPointId')?.value === 'vacio') {
          // Si hay exactamente un solo punto de venta, seleccionarlo automáticamente
          if (data.length === 1) {
            this.myForm.get('salesPointId')?.setValue(data[0].id);
          } else {
            // Si hay múltiples puntos de venta, seleccionar el primero por defecto
            this.myForm.get('salesPointId')?.setValue(data[0].id);
          }
        }
      },
      err => {
        console.error('Error al cargar puntos de venta:', err);
        this.puntoVentas = [];
        // alertamos
        this.openSnackBar('Error al cargar puntos de venta', 'Error');
      }
    )
  }

  /**
   * Obtiene todas las integraciones disponibles y hace merge con las configuradas para la tienda
   */
  getPaymentIntegrations() {
    this.loadingIntegrations = true;
    if (this.store?.id) {
      // Cargar ambos conjuntos de datos en paralelo y hacer merge
      this.loadAndMergeIntegrations(this.store.id);
    } else {
      // Solo cargar integraciones disponibles si no hay tienda
      this.storeIntegrationService.getAvailableIntegrations().subscribe(
        (data) => {
          this.availableIntegrations = data || [];
          this.storeIntegrations = [];
          this.loadingIntegrations = false;
        },
        (err) => {
          console.error('Error loading available integrations:', err);
          this.loadingIntegrations = false;
          this.openSnackBar('Error al cargar las integraciones disponibles', 'Error');
        }
      );
    }
  }

  /**
   * Carga las integraciones configuradas para una tienda específica
   */
  loadStoreIntegrations(storeId: number) {
    this.storeIntegrationService.getStoreIntegrationsWithDetails(storeId).subscribe(
      (data) => {
        this.storeIntegrations = data || [];
        this.loadingIntegrations = false;
      },
      (err) => {
        console.error('Error loading store integrations:', err);
        this.storeIntegrations = [];
        this.loadingIntegrations = false;
        this.openSnackBar('Error al cargar las integraciones de la tienda', 'Error');
      }
    );
  }

  /**
   * Carga y hace merge entre integraciones disponibles y configuradas de la tienda
   * Primero busca integraciones disponibles, solo si existen busca las configuradas para la tienda
   */
  loadAndMergeIntegrations(storeId: number) {
    
    // Primero buscar integraciones disponibles
    this.storeIntegrationService.getAvailableIntegrations().subscribe(
      (availableIntegrations) => {
        this.availableIntegrations = availableIntegrations || [];
        
        // Si existen integraciones disponibles, buscar las configuradas para la tienda
        if (this.availableIntegrations.length > 0) {
          this.storeIntegrationService.getStoreIntegrationsWithDetails(storeId).subscribe(
            (configuredIntegrations) => {
              this.storeIntegrations = configuredIntegrations || [];
              
              // Crear un merge enriquecido (opcional - para vista unificada)
              this.mergedIntegrations = this.createMergedIntegrationsView(
                this.availableIntegrations, 
                this.storeIntegrations
              );
              
              this.loadingIntegrations = false;
            },
            (err) => {
              // Manejar diferentes tipos de errores
              if (err.status === 404) {
                // Error 404 es normal - significa que la tienda no tiene configuraciones de integración aún
                this.storeIntegrations = [];
              } else {
                // Otros errores son realmente problemáticos
                console.error('Error loading store integrations:', err);
                this.storeIntegrations = [];
              }
              
              // Crear merge solo con integraciones disponibles
              this.mergedIntegrations = this.createMergedIntegrationsView(
                this.availableIntegrations, 
                []
              );
              
              this.loadingIntegrations = false;
              
              // Solo mostrar advertencia para errores que no sean 404
              if (err.status !== 404) {
                this.openSnackBar('Error al cargar las configuraciones de la tienda, pero integraciones disponibles cargadas', 'Advertencia');
              }
            }
          );
        } else {
          // No hay integraciones disponibles
          this.storeIntegrations = [];
          this.mergedIntegrations = [];
          this.loadingIntegrations = false;
        }
      },
      (err) => {
        console.error('Error loading available integrations:', err);
        this.availableIntegrations = [];
        this.storeIntegrations = [];
        this.mergedIntegrations = [];
        this.loadingIntegrations = false;
        this.openSnackBar('Error al cargar las integraciones disponibles', 'Error');
      }
    );
  }

  /**
   * Crea una vista combinada de integraciones disponibles con su estado en la tienda
   */
  createMergedIntegrationsView(available: Integration[], configured: StoreIntegration[]): any[] {
    return available.map(integration => {
      // Buscar si esta integración está configurada en la tienda
      const storeConfig = configured.find(sc => sc.integrationId === integration.id);
      
      return {
        ...integration,
        // Estado de la tienda
        isActiveInStore: storeConfig ? storeConfig.isActive : false,
        storeConfiguration: storeConfig ? storeConfig.configuration : {},
        storeIntegrationId: storeConfig ? storeConfig.id : null,
        hasStoreConfig: !!storeConfig,
        // Metadatos adicionales
        storeNotes: storeConfig ? storeConfig.notes : null,
        configuredAt: storeConfig ? storeConfig.createdAt : null,
        lastUpdated: storeConfig ? storeConfig.updatedAt : null
      };
    });
  }



  /**
   * Obtiene el nombre de una integración por su ID
   */
  getIntegrationName(integrationId: number): string {
    const integration = this.availableIntegrations.find(i => i.id === integrationId);
    return integration ? integration.name : `Integración ${integrationId}`;
  }

  /**
   * Verifica si una integración está activa para la tienda actual
   */
  isIntegrationActive(integrationId: number): boolean {
    const storeIntegration = this.storeIntegrations.find(si => si.integrationId === integrationId);
    return storeIntegration ? storeIntegration.isActive : false;
  }

  /**
   * Obtiene la configuración de una integración para la tienda actual
   */
  getIntegrationConfiguration(integrationId: number): any {
    const storeIntegration = this.storeIntegrations.find(si => si.integrationId === integrationId);
    return storeIntegration ? storeIntegration.configuration : {};
  }

  /**
   * Obtiene el ícono correspondiente a una integración
   */
  getIntegrationIcon(name: string): string {
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('mercadopago')) return 'payment';
    if (lowerName.includes('stripe')) return 'credit_card';
    if (lowerName.includes('paypal')) return 'account_balance_wallet';
    if (lowerName.includes('transferencia')) return 'account_balance';
    if (lowerName.includes('efectivo')) return 'payments';
    if (lowerName.includes('qr')) return 'qr_code';
    
    return 'payment';
  }

  /**
   * Traduce el tipo de integración al español
   */
  getIntegrationTypeLabel(type: Integration_Types): string {
    const typeLabels: { [key: string]: string } = {
      'payment': 'Método de Pago',
      'shipping': 'Envío',
      'inventory': 'Inventario',
      'accounting': 'Contabilidad',
      'crm': 'CRM',
      'email': 'Email',
      'sms': 'SMS'
    };
    return typeLabels[type] || type;
  }

  /**
   * Obtiene la etiqueta del estado de integración
   */
  getStatusLabel(isActive: boolean | undefined): string {
    return isActive ? 'Activa' : 'Inactiva';
  }

  /**
   * Alterna el estado de "Acordar con vendedor"
   */
  toggleAcordarConVendedor(event: Event) {
    const target = event.target as HTMLInputElement;
    this.acordarConVendedorActive = target.checked;
  }

  /**
   * Abre el diálogo de configuración para una integración
   */
  openIntegrationConfig(integration: Integration, event: Event) {
    event.stopPropagation();
    
    if (!this.store?.id || !integration.id) {
      this.openSnackBar('Error: Datos de tienda o integración no válidos', 'Error');
      return;
    }

    // Buscar la configuración existente
    const existingStoreIntegration = this.storeIntegrations.find(
      si => si.integrationId === integration.id
    );

    const dialogData: IntegrationConfigDialogData = {
      storeId: this.store.id,
      integration: integration,
      existingConfig: existingStoreIntegration,
      mode: existingStoreIntegration ? 'edit' : 'create'
    };

    const dialogRef = this.dialog.open(IntegrationConfigDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: dialogData,
      disableClose: true,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe((result: { configuration: any, notes: string } | undefined) => {
      if (result) {
        this.updateIntegrationConfiguration(integration.id!, result.configuration, result.notes);
      }
    });
  }

  /**
   * Actualiza la configuración de una integración
   */
  private updateIntegrationConfiguration(integrationId: number, configuration: any, notes: string) {
    if (!this.store?.id) return;

    this.storeIntegrationService.updateStoreIntegration(
      this.store.id,
      integrationId,
      { configuration, notes }
    ).subscribe(
      (updatedStoreIntegration: StoreIntegration) => {
        // Actualizar en la lista local
        const index = this.storeIntegrations.findIndex(si => si.integrationId === integrationId);
        if (index !== -1) {
          this.storeIntegrations[index] = updatedStoreIntegration;
        }
        
        this.openSnackBar('Configuración actualizada exitosamente', 'Éxito');
      },
      (error) => {
        console.error('Error al actualizar configuración:', error);
        this.openSnackBar('Error al actualizar la configuración. Intenta nuevamente.', 'Error');
      }
    );
  }

  /**
   * Crea una nueva integración
   */
  createIntegration(createDto: CreateIntegrationDto) {
    this.integrationService.createIntegration(createDto).subscribe(
      (newIntegration: Integration) => {
        this.openSnackBar('Integración creada exitosamente', 'Éxito');
        // Recargar la lista de integraciones
        this.getPaymentIntegrations();
      },
      (error) => {
        console.error('Error al crear integración:', error);
        this.openSnackBar('Error al crear la integración. Por favor, intenta nuevamente.', 'Error');
      }
    );
  }

  /**
   * Alterna el estado activo/inactivo de una integración usando store-integrations
   */
  toggleIntegrationStatus(integration: Integration, event: any) {
    // Verificar si event tiene stopPropagation antes de llamarlo
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    
    const newStatus = event.target.checked;
    const integrationId = integration.id;
    
    if (!integrationId || !this.store?.id) {
      this.openSnackBar('Error: Datos de integración o tienda no válidos', 'Error');
      // Revertir el toggle si es un elemento válido
      if (event && event.target && typeof event.target.checked !== 'undefined') {
        event.target.checked = !newStatus;
      }
      return;
    }

    const storeId = this.store.id;
    
    // Buscar si ya existe una relación store-integration
    const existingStoreIntegration = this.storeIntegrations.find(si => si.integrationId === integrationId);
    
    const targetElement = event && event.target ? event.target : null;
    
    if (existingStoreIntegration) {
      // Actualizar relación existente (PATCH)
      this.updateStoreIntegrationStatus(storeId, integrationId, newStatus, targetElement);
    } else if (newStatus) {
      // Crear nueva relación solo si se está activando (POST)
      this.createNewStoreIntegration(storeId, integrationId, targetElement);
    }
  }

  /**
   * Actualiza el estado de una store-integration existente
   */
  private updateStoreIntegrationStatus(storeId: number, integrationId: number, isActive: boolean, toggleElement: HTMLInputElement | null) {
    this.storeIntegrationService.updateStoreIntegration(storeId, integrationId, { isActive }).subscribe(
      (updatedStoreIntegration: StoreIntegration) => {
        // Actualizar en la lista local
        const index = this.storeIntegrations.findIndex(si => si.integrationId === integrationId);
        if (index !== -1) {
          this.storeIntegrations[index] = updatedStoreIntegration;
        }
        
        const statusText = isActive ? 'activada' : 'desactivada';
        this.openSnackBar(`Integración ${statusText} exitosamente`, 'Éxito');
      },
      (error) => {
        console.error('Error al actualizar estado de store-integration:', error);
        // Revertir el toggle si el elemento existe
        if (toggleElement && typeof toggleElement.checked !== 'undefined') {
          toggleElement.checked = !isActive;
        }
        this.openSnackBar('Error al cambiar el estado de la integración. Intenta nuevamente.', 'Error');
      }
    );
  }

  /**
   * Crea una nueva relación store-integration
   */
  private createNewStoreIntegration(storeId: number, integrationId: number, toggleElement: HTMLInputElement | null) {
    // Obtener configuración por defecto para el tipo de integración
    const integration = this.availableIntegrations.find(i => i.id === integrationId);
    const integrationTypeName = integration?.name?.toLowerCase() || 'default';
    const defaultConfig = DEFAULT_INTEGRATION_CONFIGS[integrationTypeName] || {};

    const createDto: CreateStoreIntegrationDto = {
      storeId: storeId,
      integrationId: integrationId,
      isActive: true,
      configuration: defaultConfig,
      notes: `Configuración inicial para ${integration?.name || 'integración'}`
    };

    this.storeIntegrationService.createStoreIntegration(createDto).subscribe(
      (newStoreIntegration: StoreIntegration) => {
        // Agregar a la lista local
        this.storeIntegrations.push(newStoreIntegration);
        this.openSnackBar('Integración activada exitosamente', 'Éxito');
      },
      (error) => {
        console.error('Error al crear store-integration:', error);
        // Revertir el toggle si el elemento existe
        if (toggleElement && typeof toggleElement.checked !== 'undefined') {
          toggleElement.checked = false;
        }
        this.openSnackBar('Error al activar la integración. Intenta nuevamente.', 'Error');
      }
    );
  }

  /**
   * Maneja el cambio de tipo de tienda (catálogo vs tienda online)
   */
  onStoreTypeChange() {
    const storeType = this.myForm.get('type')?.value;
    const isCatalog = storeType === StoreType.CATALOG;
    
    // Sincronizar campo isCatalog para compatibilidad
    this.myForm.get('isCatalog')?.setValue(isCatalog, { emitEvent: false });
    
    // Si se selecciona catálogo, limpiar integraciones
    if (isCatalog) {
      this.myForm.get('integrations')?.setValue([]);
      this.acordarConVendedorActive = false;
    }

    // Forzar detección de cambios para actualizar labels dinámicos
    // Los métodos getStoreNameLabel() y getStoreNamePlaceholder() se actualizarán automáticamente
  }

  /**
   * Valida que todos los campos obligatorios estén completos
   */
  isFormValid(): boolean {
    const formValue = this.myForm.value;
    
    // Campos obligatorios
    const requiredFields = [
      'name',        // Nombre de tienda/catálogo
      'salesPointId' // Punto de venta
    ];

    // Verificar que los campos obligatorios no estén vacíos
    const hasRequiredFields = requiredFields.every(field => {
      const value = formValue[field];
      if (field === 'salesPointId') {
        return value && value !== 'vacio';
      }
      return value !== null && value !== undefined && value !== '';
    });

    // Verificar que available tenga un valor válido - acepta tanto string como number
    const availableValue = formValue.available;
    const hasValidAvailable = availableValue !== null && availableValue !== undefined && 
                              (availableValue === '1' || availableValue === '0' || 
                               availableValue === 1 || availableValue === 0);

    // Verificar que el formulario sea válido según las validaciones de Angular
    const isAngularFormValid = this.myForm.valid;



    return hasRequiredFields && hasValidAvailable && isAngularFormValid;
  }



  /**
   * Fuerza la actualización del estado del formulario después de cambios en imágenes
   */
  private forceFormUpdate(): void {
    // Forzar detección de cambios en todo el formulario
    this.myForm.markAsDirty();
    this.myForm.markAsTouched();
    this.myForm.updateValueAndValidity();
    
    // Forzar detección de cambios en Angular
    setTimeout(() => {
      this.myForm.updateValueAndValidity();
    }, 0);
  }

  /**
   * Maneja errores HTTP específicos de manera más precisa
   */
  private handleHttpError(err: any, context: string): void {
    console.error(`Error en ${context}:`, err);
    
    let userMessage = '';
    
    if (err.status === 0) {
      userMessage = 'Sin conexión a internet. Verifique su conectividad.';
    } else if (err.status >= 500) {
      userMessage = 'Error del servidor. Intente más tarde.';
    } else if (err.status === 404) {
      userMessage = 'Recurso no encontrado.';
    } else if (err.status >= 400 && err.status < 500) {
      // Error del cliente (400-499)
      if (err.error && err.error.message) {
        userMessage = err.error.message;
      } else {
        userMessage = 'Error en la solicitud. Verifique los datos ingresados.';
      }
    } else {
      userMessage = 'Error inesperado. Intente nuevamente.';
    }
    
    this.openSnackBar(userMessage, 'Error');
  }

  /**
   * Verifica si un error es de "duplicate entry" en la base de datos
   */
  private isDuplicateEntryError(err: any): boolean {
    return err.error && 
           err.error.message && 
           (err.error.message.includes('Duplicate entry') || 
            err.error.message.includes('duplicate key') ||
            err.error.message.toLowerCase().includes('already exists'));
  }

  /**
   * Obtiene el label dinámico para el campo nombre según el tipo de tienda
   */
  getStoreNameLabel(): string {
    const storeType = this.myForm.get('type')?.value;
    return storeType === StoreType.CATALOG ? 'Nombre del catálogo' : 'Nombre de la tienda';
  }

  /**
   * Obtiene el placeholder dinámico para el campo nombre según el tipo de tienda
   */
  getStoreNamePlaceholder(): string {
    const storeType = this.myForm.get('type')?.value;
    return storeType === StoreType.CATALOG ? 'Ej: Mi Catálogo de Productos' : 'Ej: Mi Tienda Online';
  }

  /**
   * Genera el dominio por defecto basado en el nombre de la tienda
   */
  getDefaultDomain(): string {
    const storeName = this.myForm.get('name')?.value || 'mitienda';
    const cleanName = this.cleanStoreName(storeName);
    return `${cleanName}.tienda.kocursoft.com`;
  }

  /**
   * Limpia el nombre de la tienda para usar en URL
   */
  private cleanStoreName(name: string): string {
    if (!name) return 'mitienda';
    
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9\s]/g, '') // Solo letras, números y espacios
      .replace(/\s+/g, '-') // Reemplazar espacios por guiones
      .replace(/-+/g, '-') // Remover guiones múltiples
      .replace(/^-|-$/g, '') // Remover guiones al inicio y final
      .substring(0, 50); // Limitar longitud
  }

  /**
   * Alterna entre dominio por defecto y dominio personalizado
   */
  toggleCustomDomain(event: any): void {
    this.useCustomDomain = event.target.checked;
    
    if (!this.useCustomDomain) {
      // Si se deshabilita el dominio personalizado, limpiar el campo
      this.myForm.get('domain')?.setValue('');
    }
  }

  /**
   * TrackBy function para optimizar el renderizado de la lista
   */
  trackByIntegration(index: number, integration: Integration): number {
    return integration.id!;
  }

  /**
   * Maneja la selección del archivo de banner
   */
  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        this._snackBar.open('Por favor seleccione un archivo de imagen válido', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Validar tamaño (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this._snackBar.open('La imagen debe ser menor a 2MB', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Activar estado de carga
      this.bannerUploading = true;

      // Subir archivo
      this.uploadFile(file, 'banner');
    }
  }

  /**
   * Maneja la selección del archivo de favicon
   */
  onFaviconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        this._snackBar.open('Por favor seleccione un archivo de imagen válido', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Validar tamaño (máximo 500KB para favicon)
      if (file.size > 500 * 1024) {
        this._snackBar.open('El favicon debe ser menor a 500KB', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Activar estado de carga
      this.faviconUploading = true;

      // Subir archivo
      this.uploadFile(file, 'favicon');
    }
  }

  /**
   * Maneja la selección del archivo de logo
   */
  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        this._snackBar.open('Por favor seleccione un archivo de imagen válido', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Validar tamaño (máximo 1MB para logo)
      if (file.size > 1 * 1024 * 1024) {
        this._snackBar.open('El logo debe ser menor a 1MB', 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
        return;
      }

      // Activar estado de carga
      this.logoUploading = true;

      // Subir archivo
      this.uploadFile(file, 'logo');
    }
  }

  /**
   * Sube un archivo al servidor
   */
  private uploadFile(file: File, type: 'banner' | 'favicon' | 'logo'): void {
    this.archivosService.subirImagen(file)
      .then((response) => {
        if (response && response.url) {
          // Actualizar el formulario con la URL del archivo
          const fieldName = type === 'banner' ? 'bannerUrl' : type === 'favicon' ? 'faviconUrl' : 'logo';
          const control = this.myForm.get(fieldName);
          control?.setValue(response.url);
          control?.markAsTouched();
          control?.updateValueAndValidity();
          
          // Forzar detección de cambios en el formulario
          this.myForm.markAsDirty();
          this.myForm.updateValueAndValidity();
          
          const typeLabel = type === 'banner' ? 'Banner' : type === 'favicon' ? 'Favicon' : 'Logo';
          this._snackBar.open(`${typeLabel} subido correctamente`, 'Cerrar', {
            duration: 3000,
            verticalPosition: 'top'
          });
        }
      })
      .catch((error) => {
        console.error(`Error al subir ${type}:`, error);
        const errorLabel = type === 'banner' ? 'banner' : type === 'favicon' ? 'favicon' : 'logo';
        this._snackBar.open(`Error al subir ${errorLabel}`, 'Cerrar', {
          duration: 3000,
          verticalPosition: 'top'
        });
      })
      .finally(() => {
        // Desactivar estado de carga
        if (type === 'banner') {
          this.bannerUploading = false;
        } else if (type === 'favicon') {
          this.faviconUploading = false;
        } else {
          this.logoUploading = false;
        }
      });
  }

  /**
   * Obtiene la URL de vista previa del banner
   */
  getBannerPreview(): string | null {
    const bannerUrl = this.myForm.get('bannerUrl')?.value;
    return bannerUrl || null;
  }

  /**
   * Obtiene la URL de vista previa del favicon
   */
  getFaviconPreview(): string | null {
    const faviconUrl = this.myForm.get('faviconUrl')?.value;
    return faviconUrl || null;
  }

  /**
   * Obtiene la URL de vista previa del logo
   */
  getLogoPreview(): string | null {
    const logoUrl = this.myForm.get('logo')?.value;
    return logoUrl || null;
  }

  /**
   * Elimina el banner actual
   */
  deleteBanner(): void {
    // Confirmar eliminación
    if (confirm('¿Está seguro de que desea eliminar el banner actual?')) {
      // Limpiar el campo del formulario
      const bannerControl = this.myForm.get('bannerUrl');
      bannerControl?.setValue('');
      bannerControl?.markAsTouched();
      bannerControl?.updateValueAndValidity();
      
      // Limpiar el input de archivo
      if (this.bannerInput) {
        this.bannerInput.nativeElement.value = '';
      }
      
      // Forzar actualización completa del formulario
      this.forceFormUpdate();
      
      this._snackBar.open('Banner eliminado correctamente', 'Cerrar', {
        duration: 3000,
        verticalPosition: 'top'
      });
    }
  }

  /**
   * Elimina el favicon actual
   */
  deleteFavicon(): void {
    // Confirmar eliminación
    if (confirm('¿Está seguro de que desea eliminar el favicon actual?')) {
      // Limpiar el campo del formulario
      const faviconControl = this.myForm.get('faviconUrl');
      faviconControl?.setValue('');
      faviconControl?.markAsTouched();
      faviconControl?.updateValueAndValidity();
      
      // Limpiar el input de archivo
      if (this.faviconInput) {
        this.faviconInput.nativeElement.value = '';
      }
      
      // Forzar actualización completa del formulario
      this.forceFormUpdate();
      
      this._snackBar.open('Favicon eliminado correctamente', 'Cerrar', {
        duration: 3000,
        verticalPosition: 'top'
      });
    }
  }

  /**
   * Elimina el logo actual
   */
  deleteLogo(): void {
    // Confirmar eliminación
    if (confirm('¿Está seguro de que desea eliminar el logo actual?')) {
      // Limpiar el campo del formulario
      const logoControl = this.myForm.get('logo');
      logoControl?.setValue('');
      logoControl?.markAsTouched();
      logoControl?.updateValueAndValidity();
      
      // Limpiar el input de archivo
      if (this.logoInput) {
        this.logoInput.nativeElement.value = '';
      }
      
      // Forzar detección de cambios en el formulario
      this.myForm.markAsDirty();
      this.myForm.updateValueAndValidity();
      
      this._snackBar.open('Logo eliminado correctamente', 'Cerrar', {
        duration: 3000,
        verticalPosition: 'top'
      });
    }
  }

  /**
   * Cambia el tab activo
   */
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  /**
   * Verifica si un tab está activo
   */
  isTabActive(tab: string): boolean {
    return this.activeTab === tab;
  }

  /**
   * Obtiene los tabs disponibles según el tipo de tienda
   */
  getAvailableTabs(): string[] {
    const tabs = ['general', 'visual', 'payment', 'domain'];
    const storeType = this.myForm.get('type')?.value;
    return tabs;
  }

  /**
   * Navega al siguiente tab disponible
   */
  goToNextTab(): void {
    const availableTabs = this.getAvailableTabs();
    const currentIndex = availableTabs.indexOf(this.activeTab);
    if (currentIndex < availableTabs.length - 1) {
      this.setActiveTab(availableTabs[currentIndex + 1]);
    }
  }

  /**
   * Navega al tab anterior disponible
   */
  goToPreviousTab(): void {
    const availableTabs = this.getAvailableTabs();
    const currentIndex = availableTabs.indexOf(this.activeTab);
    if (currentIndex > 0) {
      this.setActiveTab(availableTabs[currentIndex - 1]);
    }
  }

  /**
   * Obtiene el progreso actual como porcentaje
   */
  getProgressPercentage(): number {
    const availableTabs = this.getAvailableTabs();
    const currentIndex = availableTabs.indexOf(this.activeTab);
    return Math.round(((currentIndex + 1) / availableTabs.length) * 100);
  }

  /**
   * Obtiene el texto del tab actual
   */
  getCurrentTabText(): string {
    const tabNames: { [key: string]: string } = {
      'general': 'Configuración General',
      'visual': 'Información Visual', 
      'payment': 'Métodos de Pago',
      'domain': 'Configuración de Dominio'
    };
    return tabNames[this.activeTab] || '';
  }

  /**
   * Maneja el error de carga del logo de integración
   */
  onLogoError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    const parentElement = imgElement.parentElement;
    
    if (imgElement && parentElement) {
      // Ocultar la imagen que falló
      imgElement.style.display = 'none';
      
      // Mostrar el ícono fallback
      const fallbackIcon = parentElement.querySelector('.integration-fallback-icon') as HTMLElement;
      if (fallbackIcon) {
        fallbackIcon.style.display = 'flex';
      }
      
      // Remover la clase has-logo del contenedor
      parentElement.classList.remove('has-logo');
    }
  }
}
